import { ActivityEntityName, EntityAdvancedFilterOperator, EntityView, MetadataEntityFieldData, MetadataEnumTypeData, SOR_CODE_TYPES } from '@jms/model';

const ITEM_STATUS_PATH = 'workOrders.codes.itemStatus';
const CODE_STATUS_PATH = 'workOrders.codes.status';
const CODE_TYPE_PATH = 'workOrders.codes.type';
const CLAIM_CODE_PATH = 'workOrders.codes.isClaimCode';

export type JobItemFilterFieldOptions = {
  sorOnly?: boolean;
};

/** Which claim bucket the active view pins, or null when it pins neither (an All tab). */
function viewClaimScope(view: EntityView | null | undefined): 'authorised' | 'pending' | null {

  const claim = view?.filters?.find(filter => filter._id === CLAIM_CODE_PATH);
  if (!claim) return null;

  const pending = claim.operator === EntityAdvancedFilterOperator.NotEquals
    ? claim.value !== true
    : claim.value === true;

  return pending ? 'pending' : 'authorised';
}

function isField(field: MetadataEntityFieldData, path: string, name: string): boolean {

  return (field.path ?? field.name) === path || field.name === name;
}

/** Type options minus SOR — Items Pro must not offer a value its board never shows. */
function withoutSorTypeOption(field: MetadataEntityFieldData): MetadataEntityFieldData {

  const options = field.typeData as MetadataEnumTypeData[];
  if (!Array.isArray(options)) return field;

  return { ...field, typeData: options.filter(option => !SOR_CODE_TYPES.includes((option.value ?? option.key) as never)) };
}

/**
 * Authorised views offer Item statuses; Pending views offer ItemPending. A view that pins neither
 * (either mount's All tab) keeps the metadata's own union, because both buckets are on screen there.
 * Applied via board `shapeFilterFields` so the filter bar follows the active view.
 *
 * SOR drops Type (its view pins `type = SOR`) and CodeStatus, leaving entity status as the only
 * status — which is why it takes the "Status" label there. Items Pro keeps both and their metadata
 * labels, so the two stay distinguishable, but loses SOR from the Type options.
 *
 * The board scope itself is enforced server-side (TenantJobItemEntityDataStrategy pins type and
 * isClaimCode); this only keeps the picker honest about what it can produce.
 */
export function shapeJobItemFilterFields(fields: MetadataEntityFieldData[], view: EntityView | null, options?: JobItemFilterFieldOptions): MetadataEntityFieldData[] {

  const claimScope = viewClaimScope(view);
  const sorOnly = options?.sorOnly === true;

  return (fields ?? [])
    .filter(field => !(sorOnly && (isField(field, CODE_STATUS_PATH, 'status') || isField(field, CODE_TYPE_PATH, 'type'))))
    .map(field => {

      if (isField(field, CODE_TYPE_PATH, 'type')) return withoutSorTypeOption(field);

      if (!isField(field, ITEM_STATUS_PATH, 'itemStatus')) return field;

      // Unpinned view: leave the metadata union alone so the picker can still reach either set.
      if (!claimScope) return sorOnly ? { ...field, displayName: 'Status' } : field;

      const entityType = claimScope === 'pending' ? ActivityEntityName.ItemPending : ActivityEntityName.Item;

      return {
        ...field,
        ...(sorOnly ? { displayName: 'Status' } : {}),
        entityOptions: { entityType },
        typeData: { ...(typeof field.typeData === 'object' && field.typeData ? field.typeData : {}), type: ActivityEntityName.EntityStatus, data: entityType },
      };
    });
}
